import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, Quote } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { api } from "@/lib/api";
import { TID } from "@/lib/testIds";
import {
  OmMark,
  DecorativeDivider,
  LotusDivider,
  SriYantra,
  Mandala,
  GopuramBand,
  BilvaLeaf,
  DiyaIcon,
  Trishul,
  Kalash,
  CornerOrnament,
  TempleArchFrame,
} from "@/components/Ornaments";
import { PanchangBanner, PanchangCard } from "@/components/Panchang";

const HERO_BG = "https://images.unsplash.com/photo-1570605505301-0f713202ca7a?crop=entropy&cs=srgb&fm=jpg&q=85&w=2200";
const HERITAGE_IMG = "https://images.pexels.com/photos/36536562/pexels-photo-36536562.jpeg";
const HERITAGE_IMG_2 = "https://images.pexels.com/photos/8819252/pexels-photo-8819252.jpeg";
const SIGNIFICANCE_IMG = "https://images.pexels.com/photos/5593067/pexels-photo-5593067.jpeg";
const FIRE_IMG = "https://images.unsplash.com/photo-1630764883473-e8c2056f0589?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const RUDRAKSHA_IMG = "https://images.pexels.com/photos/18723429/pexels-photo-18723429.jpeg";

const WHY_ICONS = [Trishul, Kalash, DiyaIcon, BilvaLeaf];

export default function Home() {
  const { t } = useTranslation();
  const [poojas, setPoojas] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [faqs, setFaqs] = useState([]);

  useEffect(() => {
    api.get("/poojas").then(({ data }) => setPoojas(data.filter((p) => p.featured)));
    api.get("/content/testimonials").then(({ data }) => setTestimonials(data));
    api.get("/content/faqs").then(({ data }) => setFaqs(data));
  }, []);

  return (
    <div data-testid="home-page">
      {/* ────────── HERO ────────── */}
      <section className="relative min-h-[92vh] flex items-end overflow-hidden">
        <img src={HERO_BG} alt="Trimbakeshwar Temple architecture" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="absolute inset-0 hero-vignette" />
        {/* Sacred geometry watermark behind the headline */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <SriYantra size={680} className="text-[#f3d7a8]" opacity={0.12} />
        </div>

        <div className="container-x relative z-10 pb-24 md:pb-32 text-[#faf8f5]">
          <div className="max-w-4xl">
            <div className="flex items-center gap-3 text-[#f3d7a8] mb-6">
              <OmMark size={32} className="text-[#f3d7a8]" />
              <span className="font-heading italic text-base md:text-lg">Acharya Guruji · Trimbakeshwar</span>
            </div>
            <PanchangBanner tone="light" />
            <div className="text-overline text-[#f3d7a8] mt-5 mb-5">{t("home.heroOverline")}</div>
            <h1 className="font-heading text-[2.5rem] leading-[1.04] sm:text-6xl lg:text-[5.5rem] lg:leading-[0.98] tracking-display max-w-4xl">
              Serving Devotees at <span className="italic-serif text-[#f3d7a8]">Trimbakeshwar</span><br />
              for Over Seven <span className="italic-serif">Generations</span>
            </h1>
            <p className="mt-7 text-base md:text-lg max-w-2xl opacity-90 leading-relaxed">
              {t("home.heroSubtitle")}
            </p>
            <div className="mt-10 flex flex-wrap gap-3 items-center">
              <Link to="/book" data-testid={TID.heroBookBtn} className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full btn-brand text-sm md:text-base font-medium">
                {t("home.heroCtaPrimary")} <ArrowRight size={16} />
              </Link>
              <Link to="/poojas" data-testid={TID.heroExploreBtn} className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm md:text-base border border-[#f3d7a8]/50 text-[#faf8f5] hover:bg-[#faf8f5]/10">
                {t("home.heroCtaSecondary")}
              </Link>
            </div>
          </div>
        </div>
        {/* Gopuram silhouette band at the very bottom of the hero */}
        <div className="absolute bottom-0 left-0 right-0 z-10 text-[#1a1514]/85">
          <GopuramBand height={20} />
        </div>
      </section>

      {/* ────────── PANCHANG (auto-hidden when disabled) ────────── */}
      <PanchangCard />

      {/* ────────── LEGACY ────────── */}
      <section className="section bg-paper relative overflow-hidden">
        <div className="absolute -left-32 top-12 text-[color:var(--brand)] pointer-events-none hidden md:block">
          <Mandala size={520} opacity={0.05} />
        </div>
        <div className="container-x grid md:grid-cols-12 gap-12 lg:gap-16 items-center relative">
          <div className="order-2 md:order-1 md:col-span-7">
            <DecorativeDivider label={t("home.legacyOverline")} className="mb-8 justify-start" />
            <h2 className="font-heading text-4xl md:text-6xl tracking-display leading-[1.05] max-w-2xl">
              Seven Generations of <em className="italic-serif">Unbroken</em> Vedic Tradition
            </h2>
            <p className="mt-7 text-[16px] md:text-[17px] leading-[1.85] text-[color:var(--ink-2)] max-w-xl drop-cap">
              {t("home.legacyBody")}
            </p>

            <div className="mt-10 grid grid-cols-3 gap-8 max-w-md">
              {[
                { v: "7", k: "Generations", sub: "of Vedic service" },
                { v: "500K+", k: "Devotees", sub: "across the world" },
                { v: "9", k: "Sacred Rituals", sub: "performed daily" },
              ].map((s) => (
                <div key={s.k} className="border-l border-[color:var(--border-strong)] pl-4">
                  <div className="font-heading text-5xl text-[color:var(--brand)] leading-none">{s.v}</div>
                  <div className="text-[10px] uppercase tracking-[0.22em] opacity-70 mt-2">{s.k}</div>
                  <div className="text-xs opacity-60 mt-0.5 italic-serif">{s.sub}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="order-1 md:order-2 md:col-span-5 relative">
            {/* Layered image composition */}
            <div className="relative">
              <TempleArchFrame ratio="aspect-[4/5]" className="border border-[color:var(--border-strong)] shadow-[0_30px_60px_-30px_rgba(26,21,20,0.4)]">
                <img src={HERITAGE_IMG} alt="Ancient temple carvings" className="w-full h-full object-cover" />
              </TempleArchFrame>
              {/* Floating secondary image */}
              <div className="hidden lg:block absolute -bottom-10 -left-12 w-44 h-44 rounded-md overflow-hidden border-4 border-[color:var(--bg)] shadow-[0_20px_40px_-20px_rgba(26,21,20,0.4)]">
                <img src={HERITAGE_IMG_2} alt="Sacred offerings" className="w-full h-full object-cover" />
              </div>
              {/* Decorative seal */}
              <div className="absolute -top-6 -right-6 w-20 h-20 rounded-full bg-[color:var(--brand)] text-[#faf8f5] flex items-center justify-center shadow-lg">
                <div className="text-center">
                  <div className="font-heading text-3xl leading-none">7</div>
                  <div className="text-[7px] tracking-[0.22em] uppercase mt-1">Generations</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ────────── FEATURED POOJAS ────────── */}
      <section className="section bg-paper-strong relative overflow-hidden">
        <CornerOrnament corner="tr" element="yantra" />
        <div className="container-x relative">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <DecorativeDivider label={t("home.featuredOverline")} className="mb-5" />
            <h2 className="font-heading text-4xl md:text-6xl tracking-display leading-[1.05]">
              Featured <em className="italic-serif text-[color:var(--brand)]">Poojas</em> at Trimbakeshwar
            </h2>
            <p className="mt-5 text-[color:var(--ink-2)]">{t("home.featuredSubtitle")}</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-7">
            {poojas.map((p, idx) => (
              <Link
                key={p.slug}
                to={`/poojas/${p.slug}`}
                data-testid={`featured-pooja-${p.slug}`}
                className="card-pooja rounded-md overflow-hidden flex flex-col group p-3"
              >
                <div className="arch-mask aspect-[3/4] relative">
                  <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1a1514]/70 via-transparent to-transparent" />
                  {/* Small index seal */}
                  <div className="absolute top-3 left-3 w-9 h-9 rounded-full bg-[#faf8f5]/95 flex items-center justify-center font-heading italic-serif text-[color:var(--brand)]">
                    {String(idx + 1).padStart(2, "0")}
                  </div>
                  {/* Bottom title overlay */}
                  <div className="absolute inset-x-0 bottom-0 p-4 text-[#faf8f5]">
                    <h3 className="font-heading text-xl md:text-[1.4rem] leading-tight">{p.name}</h3>
                  </div>
                </div>
                <div className="p-4 pt-5 flex flex-col flex-1">
                  <p className="text-[13px] opacity-75 leading-relaxed line-clamp-3 flex-1">{p.short_description}</p>
                  <div className="mt-4 pt-4 border-t border-dashed border-[color:var(--border-strong)] flex items-end justify-between">
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.22em] opacity-60">{t("poojas.startingFrom")}</div>
                      <div className="font-heading italic-serif text-2xl text-[color:var(--brand)] mt-0.5">
                        {t("common.rupee")}{p.price.toLocaleString("en-IN")}
                      </div>
                    </div>
                    <ArrowRight size={16} className="text-[color:var(--brand)] group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="mt-12 text-center">
            <Link to="/poojas" data-testid="view-all-poojas" className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full btn-ghost-brand text-sm">
              View All Sacred Rituals <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      <LotusDivider className="py-8 bg-paper" />

      {/* ────────── WHY CHOOSE US ────────── */}
      <section className="section bg-paper relative overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-[color:var(--brand)] pointer-events-none">
          <Mandala size={780} opacity={0.04} />
        </div>
        <div className="container-x relative">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <DecorativeDivider label={t("home.whyOverline")} className="mb-5" />
            <h2 className="font-heading text-4xl md:text-6xl tracking-display leading-[1.05]">
              An <em className="italic-serif text-[color:var(--brand)]">Authority</em> on Trimbakeshwar Rituals
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-[color:var(--border-strong)] border border-[color:var(--border-strong)] rounded-md overflow-hidden">
            {[
              { Icon: WHY_ICONS[0], k: "lineage", num: "1" },
              { Icon: WHY_ICONS[1], k: "scripture", num: "2" },
              { Icon: WHY_ICONS[2], k: "guidance", num: "3" },
              { Icon: WHY_ICONS[3], k: "transparency", num: "4" },
            ].map(({ Icon, k, num }) => (
              <div key={k} className="bg-[color:var(--bg)] p-8 flex flex-col">
                <div className="flex items-baseline justify-between text-[color:var(--brand)] mb-5">
                  <Icon size={36} />
                  <span className="font-heading italic-serif text-2xl opacity-50">{num}</span>
                </div>
                <h3 className="font-heading text-[1.6rem] leading-tight tracking-display">{t(`home.whyItems.${k}`)}</h3>
                <div className="gilt-rule my-4 w-16" />
                <p className="text-[14px] opacity-80 leading-relaxed">{t(`home.whyItems.${k}Body`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ────────── SIGNIFICANCE ────────── */}
      <section className="section bg-manuscript relative overflow-hidden">
        <div className="container-x grid md:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div className="relative">
            <div className="frame-gilt">
              <TempleArchFrame ratio="aspect-[4/5]" className="">
                <img src={SIGNIFICANCE_IMG} alt="Sacred Trimbakeshwar surroundings" className="w-full h-full object-cover" />
              </TempleArchFrame>
            </div>
            {/* Small detail stack */}
            <div className="hidden md:flex absolute -bottom-12 -right-8 gap-3">
              <div className="w-24 h-24 rounded-md overflow-hidden border-4 border-[color:var(--bg)] shadow-md">
                <img src={FIRE_IMG} alt="Sacred fire" className="w-full h-full object-cover" />
              </div>
              <div className="w-24 h-24 rounded-md overflow-hidden border-4 border-[color:var(--bg)] shadow-md">
                <img src={RUDRAKSHA_IMG} alt="Rudraksha beads" className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
          <div>
            <DecorativeDivider label={t("home.significanceOverline")} className="mb-5 justify-start" />
            <h2 className="font-heading text-4xl md:text-6xl tracking-display leading-[1.05]">
              Where the <em className="italic-serif text-[color:var(--brand)]">Godavari</em> Begins and <em className="italic-serif">Shiva</em> Resides
            </h2>
            <p className="mt-6 leading-[1.85] text-[color:var(--ink-2)] text-[16px] drop-cap">{t("home.significanceBody")}</p>
            <ul className="mt-7 space-y-2 text-sm">
              {[
                "Three-faced Shivalinga representing Brahma, Vishnu and Mahesh",
                "Origin of the sacred Godavari river at Kushavarta Kund",
                "Cradled by the Brahmagiri Hills — abode of Lord Brahma",
              ].map((line, i) => (
                <li key={i} className="flex gap-3 items-baseline">
                  <span className="text-[color:var(--brand)] font-heading italic-serif text-xl leading-none">·</span>
                  <span className="opacity-85">{line}</span>
                </li>
              ))}
            </ul>
            <Link to="/knowledge" className="mt-8 inline-flex items-center gap-2 px-5 py-2.5 rounded-full btn-ghost-brand text-sm" data-testid="knowledge-cta">
              Explore Knowledge Hub <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* ────────── TESTIMONIALS ────────── */}
      <section className="section bg-paper-strong relative overflow-hidden">
        <div className="container-x relative">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <DecorativeDivider label={t("home.testimonialsOverline")} className="mb-5" />
            <h2 className="font-heading text-4xl md:text-6xl tracking-display leading-[1.05]">
              Stories from <em className="italic-serif text-[color:var(--brand)]">Our Devotees</em>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.slice(0, 6).map((tm, i) => (
              <div key={i} className="card-manuscript rounded-md p-7 relative">
                <Quote className="text-[color:var(--brand)] opacity-40" size={24} />
                <p className="mt-4 text-[15px] leading-[1.8] italic-serif text-[color:var(--ink-2)]">"{tm.quote}"</p>
                <div className="gilt-rule my-5 w-12" />
                <div>
                  <div className="font-heading text-lg leading-tight">{tm.name}</div>
                  <div className="text-[11px] tracking-[0.18em] uppercase opacity-65 mt-1">{tm.location} · {tm.pooja}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ────────── FAQ ────────── */}
      <section className="section bg-paper relative overflow-hidden">
        <div className="container-x grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <DecorativeDivider label={t("home.faqOverline")} className="mb-5 justify-start" />
            <h2 className="font-heading text-4xl md:text-5xl tracking-display leading-[1.05]">
              Questions Devotees <em className="italic-serif text-[color:var(--brand)]">Often Ask</em>
            </h2>
            <p className="mt-5 opacity-80 max-w-md">If your question is not listed here, speak to our acharyas directly. We respond to every devotee personally.</p>
            <Link to="/contact" className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full btn-ghost-brand text-sm" data-testid="faq-contact-cta">
              Ask a Question <ArrowRight size={14} />
            </Link>
          </div>
          <div className="md:col-span-7">
            <Accordion type="single" collapsible className="space-y-3">
              {faqs.map((f, i) => (
                <AccordionItem
                  key={i}
                  value={`q-${i}`}
                  data-testid={`home-faq-${i}`}
                  className="border border-[color:var(--border-strong)] rounded-md bg-white px-6 data-[state=open]:border-[color:var(--brand)]/50 data-[state=open]:bg-[color:var(--bg)]"
                >
                  <AccordionTrigger className="text-left font-heading text-xl md:text-2xl tracking-display py-5 hover:no-underline">
                    <span className="flex items-baseline gap-3">
                      <span className="font-heading italic-serif text-[color:var(--brand)]/60 text-base">{String(i + 1).padStart(2, "0")}</span>
                      {f.question}
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-[15px] opacity-85 leading-[1.85] pb-6 pl-[2.2rem]">
                    {f.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* ────────── CTA BANNER ────────── */}
      <section className="relative overflow-hidden bg-ink">
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <SriYantra size={620} className="text-[#f3d7a8]" opacity={0.12} />
        </div>
        <div className="text-[#f3d7a8]/60">
          <GopuramBand height={20} />
        </div>
        <div className="container-x py-20 md:py-28 text-center relative z-10">
          <OmMark size={56} className="mx-auto text-[#f3d7a8] mb-5" />
          <DecorativeDivider label="ॐ नमः शिवाय" className="mb-6 text-[#f3d7a8]" />
          <h2 className="font-heading text-4xl md:text-6xl tracking-display leading-[1.05] max-w-2xl mx-auto text-[#faf8f5]">
            Begin Your <em className="italic-serif text-[#f3d7a8]">Sacred Journey</em>
          </h2>
          <p className="mt-5 max-w-xl mx-auto text-[#f3e7c8]/85">{t("home.ctaBody")}</p>
          <Link to="/book" className="mt-9 inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-[#f3d7a8] text-[#5e1b17] font-medium hover:bg-[#fce5ba]" data-testid="cta-book-btn">
            {t("home.ctaButton")} <ArrowRight size={16} />
          </Link>
        </div>
        <div className="text-[#f3d7a8]/60 rotate-180">
          <GopuramBand height={20} />
        </div>
      </section>
    </div>
  );
}
